import React, { FC, useEffect, useMemo, useState } from "react";
import { Button, Col, InputNumber, message, Row, Select, Space, Table, Upload } from "antd";
import {
  CloudDownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
} from "@ant-design/icons";

type AyushmanImplantTableProps = {
  implantOptions: any[];
  uploadedImplantFileList: any[];
  isDisabled?: boolean;
  onUploadFile: (implantOption: any, quantity: number, file: File) => void;
  onPreview: (documentUrl: string) => void;
  onDownload: (documentName: string, documentUrl: string) => void;
  onDelete: (
    documentType: string,
    documentUrl: string,
  ) => void | Promise<void>;
};

type AddedImplantRow = {
  key: string;
  option: any;
  quantity: number;
};

const getOptionKey = (option: any) =>
  String(
    option?.labelId ??
      option?.id ??
      option?.documentUniqueCode ??
      option?.code ??
      option?.name ??
      "",
  );

const matchUploadedDoc = (option: any, uploads: any[] = []) =>
  (uploads || []).find((u) => {
    const optionKeys = [
      option?.labelId,
      option?.id,
      option?.documentUniqueCode,
      option?.code,
    ]
      .filter((k) => k !== undefined && k !== null && String(k) !== "")
      .map(String);
    const docKeys = [u?.documentType, u?.documentUniqueCode]
      .filter((k) => k !== undefined && k !== null && String(k) !== "")
      .map(String);
    if (optionKeys.some((k) => docKeys.includes(k))) return true;

    const optionLabels = [
      option?.label,
      option?.name,
      option?.documentMasterName,
    ]
      .filter(Boolean)
      .map((s) => String(s).toLowerCase().trim());
    const uploadedLabel = String(
      u?.documentLabelType || u?.documentMasterName || "",
    )
      .toLowerCase()
      .trim();
    // Also match "Name (code)" style labels loosely by name part
    if (Boolean(uploadedLabel) && optionLabels.includes(uploadedLabel)) {
      return true;
    }
    const uploadedNameOnly = uploadedLabel.split("(")[0]?.trim();
    return (
      Boolean(uploadedNameOnly) &&
      optionLabels.some(
        (label) =>
          label === uploadedNameOnly ||
          label.split("(")[0]?.trim() === uploadedNameOnly,
      )
    );
  });

const AyushmanImplantTable: FC<AyushmanImplantTableProps> = ({
  implantOptions,
  uploadedImplantFileList,
  isDisabled,
  onUploadFile,
  onPreview,
  onDownload,
  onDelete,
}) => {
  const [selectedOptionKey, setSelectedOptionKey] = useState<string | null>(
    null,
  );
  const [addQuantity, setAddQuantity] = useState<number>(1);
  const [addedRows, setAddedRows] = useState<AddedImplantRow[]>([]);

  const optionsByKey = useMemo(() => {
    const map = new Map<string, any>();
    (implantOptions || []).forEach((option) => {
      const key = getOptionKey(option);
      if (key) map.set(key, option);
    });
    return map;
  }, [implantOptions]);

  // Keep one row per implant: uploads are source of truth; keep pending adds only
  useEffect(() => {
    setAddedRows((prev) => {
      const byKey = new Map<string, AddedImplantRow>();

      (uploadedImplantFileList || []).forEach((doc) => {
        const matchedOption = (implantOptions || []).find((option) =>
          matchUploadedDoc(option, [doc]),
        );
        const prevMatch = prev.find((row) =>
          matchUploadedDoc(row.option, [doc]),
        );

        const option =
          matchedOption ||
          prevMatch?.option || {
            labelId: doc?.documentType,
            id: doc?.documentType,
            label:
              doc?.documentLabelType || doc?.documentMasterName || "Implant",
            name:
              doc?.documentLabelType || doc?.documentMasterName || "Implant",
            documentUniqueCode: doc?.documentUniqueCode,
            code: doc?.documentUniqueCode,
            amount: doc?.amount,
            maxImplantsAllowed: doc?.maxImplantsAllowed,
          };

        const key =
          (matchedOption && getOptionKey(matchedOption)) ||
          prevMatch?.key ||
          getOptionKey(option) ||
          String(doc?.documentUrl || doc?.id || "");

        if (!key) return;

        const qty = Number(doc?.implantQuantity);
        byKey.set(key, {
          key,
          option: {
            ...(byKey.get(key)?.option || {}),
            ...option,
          },
          quantity:
            Number.isFinite(qty) && qty > 0
              ? qty
              : byKey.get(key)?.quantity || prevMatch?.quantity || 1,
        });
      });

      // Keep locally added rows that don't have an uploaded file yet
      prev.forEach((row) => {
        if (!row?.key || byKey.has(row.key)) return;
        const alreadyShown = Array.from(byKey.values()).some(
          (existing) =>
            getOptionKey(existing.option) === getOptionKey(row.option) ||
            matchUploadedDoc(row.option, [
              {
                documentType: existing.option?.labelId,
                documentUniqueCode: existing.option?.documentUniqueCode,
                documentLabelType: existing.option?.label || existing.option?.name,
              },
            ]),
        );
        if (!alreadyShown) {
          byKey.set(row.key, row);
        }
      });

      return Array.from(byKey.values());
    });
  }, [uploadedImplantFileList, implantOptions]);

  const selectedOption = selectedOptionKey
    ? optionsByKey.get(selectedOptionKey)
    : null;
  const selectedMaxAllowed = Number(selectedOption?.maxImplantsAllowed || 0);

  const availableOptions = useMemo(() => {
    const addedKeys = new Set(addedRows.map((row) => row.key));
    return (implantOptions || []).filter((option) => {
      const key = getOptionKey(option);
      return key && !addedKeys.has(key);
    });
  }, [implantOptions, addedRows]);

  const handleAdd = () => {
    if (!selectedOption || !selectedOptionKey) {
      message.warning("Please select an implant");
      return;
    }
    let qty = Number(addQuantity) > 0 ? Number(addQuantity) : 1;
    if (selectedMaxAllowed > 0 && qty > selectedMaxAllowed) {
      message.warning(
        `Quantity cannot exceed max implants allowed (${selectedMaxAllowed})`,
      );
      qty = selectedMaxAllowed;
    }

    setAddedRows((prev) => [
      ...prev,
      {
        key: selectedOptionKey,
        option: selectedOption,
        quantity: qty,
      },
    ]);
    setSelectedOptionKey(null);
    setAddQuantity(1);
  };

  const handleRemoveRow = async (row: AddedImplantRow) => {
    const uploaded = matchUploadedDoc(row.option, uploadedImplantFileList);
    // Deleting implant row also deletes its uploaded document
    if (uploaded?.documentUrl) {
      await onDelete(
        String(
          uploaded?.documentType ||
            row?.option?.labelId ||
            row?.option?.id ||
            "",
        ),
        uploaded.documentUrl,
      );
    }
    setAddedRows((prev) => prev.filter((item) => item.key !== row.key));
  };

  const dataSource = addedRows.map((row) => ({
    ...row,
    uploaded: matchUploadedDoc(row.option, uploadedImplantFileList),
  }));

  const columns = [
    {
      title: "No.",
      key: "sno",
      align: "center" as const,
      width: 70,
      render: (_: any, __: any, index: number) => (
        <span style={{ fontWeight: "bold" }}>{index + 1}</span>
      ),
    },
    {
      title: "Name",
      key: "name",
      align: "center" as const,
      render: (_: any, record: any) => (
        <div style={{ textTransform: "initial", fontSize: "15px" }}>
          {record?.option?.name || record?.option?.label || "-"}
        </div>
      ),
    },
    {
      title: "Code",
      key: "code",
      align: "center" as const,
      width: 140,
      render: (_: any, record: any) => (
        <div style={{ textTransform: "initial", fontSize: "15px" }}>
          {record?.option?.code || record?.option?.documentUniqueCode || "-"}
        </div>
      ),
    },
    {
      title: "Amount",
      key: "amount",
      align: "center" as const,
      width: 120,
      render: (_: any, record: any) => {
        const amount = record?.option?.amount;
        if (amount === null || amount === undefined || amount === "") {
          return <span>-</span>;
        }
        return (
          <div style={{ textTransform: "initial", fontSize: "15px" }}>
            ₹ {amount}
          </div>
        );
      },
    },
    {
      title: "Max Allowed",
      key: "maxImplantsAllowed",
      align: "center" as const,
      width: 120,
      render: (_: any, record: any) => (
        <div style={{ textTransform: "initial", fontSize: "15px" }}>
          {record?.option?.maxImplantsAllowed ?? "-"}
        </div>
      ),
    },
    {
      title: "Quantity",
      key: "quantity",
      align: "center" as const,
      width: 100,
      render: (_: any, record: any) => (
        <div style={{ textTransform: "initial", fontSize: "15px" }}>
          {record?.quantity ?? 1}
        </div>
      ),
    },
    {
      title: "Document",
      key: "document",
      align: "center" as const,
      render: (_: any, record: any) => {
        if (record?.uploaded?.documentName) {
          return (
            <div style={{ textTransform: "initial", fontSize: "15px" }}>
              {record.uploaded.documentName}
            </div>
          );
        }
        return (
          <Upload
            beforeUpload={() => false}
            showUploadList={false}
            maxCount={1}
            disabled={isDisabled}
            onChange={(info) => {
              const file = (info?.file as any)?.originFileObj || info?.file;
              if (!file) return;
              onUploadFile(record.option, record.quantity || 1, file as File);
            }}
          >
            <Button icon={<UploadOutlined />} disabled={isDisabled}>
              Upload
            </Button>
          </Upload>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      align: "center" as const,
      width: 160,
      render: (_: any, record: any) => {
        const uploaded = record?.uploaded;
        return (
          <Space size="middle">
            {uploaded?.documentUrl ? (
              <>
                <EyeOutlined
                  style={{
                    color: "#008B8B",
                    fontSize: "17px",
                    cursor: "pointer",
                  }}
                  onClick={() => onPreview(uploaded.documentUrl)}
                />
                <CloudDownloadOutlined
                  style={{
                    color: "#008B8B",
                    fontSize: "17px",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    uploaded?.documentName &&
                    onDownload(uploaded.documentName, uploaded.documentUrl)
                  }
                />
              </>
            ) : null}
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={isDisabled}
              onClick={() => handleRemoveRow(record)}
            />
          </Space>
        );
      },
    },
  ];

  if (!(implantOptions || []).length && !addedRows.length) return null;

  return (
    <>
      <h3 style={{ margin: "28px 0 14px" }}>IMPLANTS</h3>

      <Row
        gutter={16}
        align="bottom"
        wrap={false}
        style={{ marginBottom: 16, width: "100%" }}
      >
        <Col flex="auto">
          <div style={{ marginBottom: 4, fontWeight: 600 }}>Select Implant</div>
          <Select
            style={{ width: "100%" }}
            placeholder="Select Implant"
            value={selectedOptionKey}
            disabled={isDisabled}
            allowClear
            showSearch
            optionFilterProp="label"
            onChange={(value) => {
              setSelectedOptionKey(value || null);
              setAddQuantity(1);
            }}
            options={availableOptions.map((option) => ({
              value: getOptionKey(option),
              label: option?.label || option?.name || getOptionKey(option),
            }))}
          />
        </Col>
        <Col flex="140px">
          <div style={{ marginBottom: 4, fontWeight: 600 }}>Quantity</div>
          <InputNumber
            style={{ width: "100%" }}
            min={1}
            max={selectedMaxAllowed > 0 ? selectedMaxAllowed : undefined}
            precision={0}
            value={addQuantity}
            disabled={isDisabled || !selectedOptionKey}
            onKeyPress={(e) => {
              if (!/[0-9]/.test(e.key)) e.preventDefault();
            }}
            onChange={(value) => {
              let nextQty = Number(value) > 0 ? Number(value) : 1;
              if (selectedMaxAllowed > 0 && nextQty > selectedMaxAllowed) {
                nextQty = selectedMaxAllowed;
              }
              setAddQuantity(nextQty);
            }}
          />
        </Col>
        <Col flex="100px">
          <Button
            type="primary"
            style={{
              backgroundColor: "rgb(0, 84, 157)",
              color: "#fff",
              width: "100%",
            }}
            disabled={isDisabled || !selectedOptionKey}
            onClick={handleAdd}
          >
            ADD
          </Button>
        </Col>
      </Row>

      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        bordered
        className="claim-table"
        locale={{ emptyText: "No implants added" }}
        style={{ width: "100%", textAlign: "center" }}
      />
    </>
  );
};

export default AyushmanImplantTable;
